import { useContext, useEffect, useMemo, useRef, useState } from 'react'

import { Button, IconButton } from '@material-ui/core'
import { Delete } from '@material-ui/icons'
import AddIcon from '@material-ui/icons/Add'
import { EditContext } from 'Components/Hooks/ContextEdit'
import SetState from 'Components/Hooks/SetState'
import BasicTable from 'Components/UI/Atoms/BasicTable'
import useCustomSnackbar from 'Components/UI/Atoms/SnackBar'
import CancelConfirmButtons from 'Components/UI/Molecules/CancelConfirmButtons'
import InputsWithoutValidate from 'Components/UI/Molecules/InputsWithoutValidate'
import InputsWithValidate from 'Components/UI/Molecules/InputsWithValidate'
import DialogComponent from 'Components/UI/Molecules/Modals/DialogComponent'
import { Form, Formik } from 'formik'
import { interfaces } from 'service/interfaces'
import * as yup from 'yup'

const EMPRESA_ID_BY_LABEL = {
  ECO: 1,
  OEM: 2,
  DOS: 3,
}

const CURRENCIES = [
  { id: 1, label: 'Dolares' },
  { id: 2, label: 'Euros' },
  { id: 3, label: 'Reales' },
]

const ALLOWED_METHODS = [1, 2, 3, 4, 6, 7, 8, 51, 60]

const toNumber = (value) => {
  const num = Number(value)
  return Number.isFinite(num) ? num : 0
}

const getEmpresaId = (origen) => {
  if (typeof origen === 'number') return origen
  const normalized = String(origen || '')
    .trim()
    .toUpperCase()
  return EMPRESA_ID_BY_LABEL[normalized] || 1
}

const buildMethodNameMap = (metodosPago = []) => {
  return metodosPago.reduce((acc, item) => {
    acc[item.id] = item.tipo
    return acc
  }, {})
}

const normalizeMethodRow = (row = {}, methodNameMap = {}) => {
  const tipo = toNumber(row.tipo)
  return {
    id: row.id,
    tipo,
    metodo: methodNameMap[tipo] || row.metodo || '',
    descripcion: row.detalle || row.descripcion || '',
    importe: toNumber(row.importe),
    origen: row.origen,
    lote: row.lote,
    cupon: row.cupon,
    moneda: row.moneda,
  }
}

const AddPaymentMethod = ({
  open,
  handleClose,
  onSubmit,
  methodOptions,
  debitCards,
  creditCards,
  banks,
}) => {
  const formikRef = useRef(null)

  const validationSchema = yup.object().shape({
    metodo: yup.number().required('Campo obligatorio'),
    importe: yup
      .number()
      .typeError('Debe ser numérico')
      .moreThan(0, 'Debe ser mayor a 0')
      .required('Campo obligatorio'),
    tarjeta: yup.number().when('metodo', {
      is: (val) => [2, 3].includes(Number(val)),
      then: (schema) => schema.required('Campo obligatorio'),
      otherwise: (schema) => schema.nullable(),
    }),
    lote: yup.string().when('metodo', {
      is: (val) => [2, 3].includes(Number(val)),
      then: (schema) => schema.trim().required('Campo obligatorio'),
      otherwise: (schema) => schema.trim(),
    }),
    cupon: yup.string().when('metodo', {
      is: (val) => [2, 3].includes(Number(val)),
      then: (schema) => schema.trim().required('Campo obligatorio'),
      otherwise: (schema) => schema.trim(),
    }),
    nro_operacion: yup.string().when('metodo', {
      is: (val) => [4, 6, 7].includes(Number(val)),
      then: (schema) => schema.trim().required('Campo obligatorio'),
      otherwise: (schema) => schema.trim(),
    }),
    banco: yup.number().when('metodo', {
      is: (val) => [6, 7].includes(Number(val)),
      then: (schema) => schema.required('Campo obligatorio'),
      otherwise: (schema) => schema.nullable(),
    }),
    moneda: yup.number().when('metodo', {
      is: (val) => Number(val) === 8,
      then: (schema) => schema.required('Campo obligatorio'),
      otherwise: (schema) => schema.nullable(),
    }),
    cotizacion: yup.number().when('metodo', {
      is: (val) => Number(val) === 8,
      then: (schema) => schema.moreThan(0, 'Debe ser mayor a 0').required('Campo obligatorio'),
      otherwise: (schema) => schema.nullable(),
    }),
  })

  const fields = (values, setFieldValue) => {
    const fieldsData = [
      {
        class: 'autocomplete',
        name: 'metodo',
        label: 'Método de pago',
        optionValue: 'id',
        optionLabel: `options["label"]`,
        options: methodOptions,
        value: values.metodo,
        sm: 12,
        xs: 12,
        onChange: (value) => {
          setFieldValue('metodo', value?.id || '')
          setFieldValue('tarjeta', '')
          setFieldValue('lote', '')
          setFieldValue('cupon', '')
          setFieldValue('nro_operacion', '')
          setFieldValue('descripcion', '')
          setFieldValue('banco', banks[0]?.id || '')
          setFieldValue('moneda', CURRENCIES[0]?.id || '')
          setFieldValue('cotizacion', '')
        },
      },
    ]

    const metodo = Number(values.metodo)
    const importeField = {
      class: 'textfield',
      name: 'importe',
      label: 'Importe en pesos',
      type: 'number',
      value: values.importe,
      sm: [51, 60, 1].includes(metodo) ? 12 : 6,
      xs: 12,
    }

    const descripcionField = {
      class: 'textfield',
      name: 'descripcion',
      label: 'Descripción',
      value: values.descripcion,
      sm: [6, 7, 8].includes(metodo) ? 6 : 12,
      xs: 12,
      multiline: true,
      rows: 2,
    }

    if (metodo === 1) {
      fieldsData.push(importeField, descripcionField)
      return fieldsData
    }

    if (metodo === 4) {
      fieldsData.push(
        importeField,
        {
          class: 'textfield',
          name: 'nro_operacion',
          label: 'N° operación',
          type: 'number',
          value: values.nro_operacion,
          sm: 6,
          xs: 12,
        },
        descripcionField
      )
      return fieldsData
    }

    if ([2, 3].includes(metodo)) {
      const options = metodo === 2 ? debitCards : creditCards
      fieldsData.push(
        {
          class: 'autocomplete',
          name: 'tarjeta',
          label: 'Tarjeta',
          optionValue: 'id',
          optionLabel: `options["label"]`,
          options,
          value: values.tarjeta,
          sm: 6,
          xs: 12,
        },
        importeField,
        {
          class: 'textfield',
          name: 'lote',
          label: 'Lote',
          type: 'number',
          value: values.lote,
          sm: 6,
          xs: 12,
        },
        {
          class: 'textfield',
          name: 'cupon',
          label: 'Cupón',
          type: 'number',
          value: values.cupon,
          sm: 6,
          xs: 12,
        },
        descripcionField
      )
      return fieldsData
    }

    if ([6, 7].includes(metodo)) {
      fieldsData.push({
        class: 'autocomplete',
        name: 'banco',
        label: 'Banco',
        optionValue: 'id',
        optionLabel: `options["label"]`,
        options: banks,
        value: values.banco,
        sm: [6, 7].includes(metodo) ? 12 : 6,
        xs: 12,
      })
      fieldsData.push(
        {
          ...importeField,
          sm: [6, 7].includes(metodo) ? 12 : importeField.sm,
        },
        {
          class: 'textfield',
          name: 'nro_operacion',
          label: 'N° operación',
          type: 'number',
          value: values.nro_operacion,
          sm: [6, 7].includes(metodo) ? 12 : 6,
          xs: 12,
        },
        {
          ...descripcionField,
          sm: [6, 7].includes(metodo) ? 12 : descripcionField.sm,
        }
      )
      return fieldsData
    }

    if (metodo === 8) {
      fieldsData.push(
        {
          ...importeField,
          sm: 12,
        },
        {
          class: 'autocomplete',
          name: 'moneda',
          label: 'Moneda',
          optionValue: 'id',
          optionLabel: `options["label"]`,
          options: CURRENCIES,
          value: values.moneda,
          sm: 12,
          xs: 12,
        },
        {
          class: 'textfield',
          name: 'cotizacion',
          label: 'Cotización',
          type: 'number',
          value: values.cotizacion,
          sm: 12,
          xs: 12,
        },
        {
          ...descripcionField,
          sm: 12,
        }
      )
      return fieldsData
    }

    // Fallback para tipos adicionales (ej: 51, 60)
    fieldsData.push(importeField, descripcionField)

    return fieldsData
  }

  return (
    <DialogComponent
      open={open}
      handleclose={handleClose}
      title="Agregar método de pago"
      disableBackdropClick
      disableEscapeKeyDown
      widthLarge="500px"
      disableChildHeight
      children={
        <Formik
          innerRef={formikRef}
          initialValues={{
            metodo: methodOptions[0]?.id || '',
            importe: '',
            tarjeta: '',
            lote: '',
            cupon: '',
            nro_operacion: '',
            descripcion: '',
            banco: banks[0]?.id || '',
            moneda: CURRENCIES[0].id,
            cotizacion: '',
          }}
          validationSchema={validationSchema}
          onSubmit={async (values, helpers) => {
            const success = await onSubmit(values)
            helpers.setSubmitting(false)
            if (success) handleClose()
          }}
          enableReinitialize
        >
          {({ values, setFieldValue, isSubmitting }) => (
            <Form>
              <InputsWithValidate
                fields={fields(values, setFieldValue)}
                spacing={2}
                setFieldValue={setFieldValue}
                isSubmitting={isSubmitting}
              />
            </Form>
          )}
        </Formik>
      }
      actions={
        <CancelConfirmButtons
          onClickSubmit={() => {
            if (formikRef.current) formikRef.current.submitForm()
          }}
          onClickCancel={handleClose}
          textButtonSubmit="CONFIRMAR"
        />
      }
    />
  )
}

const EditPaymentMethod = ({ open, handleClose, row, onSubmit }) => {
  const formikRef = useRef(null)

  return (
    <DialogComponent
      open={open}
      handleclose={handleClose}
      title="Editar método de pago"
      disableBackdropClick
      disableEscapeKeyDown
      widthLarge="500px"
      disableChildHeight
      children={
        <Formik
          innerRef={formikRef}
          initialValues={{
            descripcion: row?.descripcion || '',
            importe: row?.importe || 0,
          }}
          enableReinitialize
          validationSchema={yup.object().shape({
            importe: yup
              .number()
              .typeError('Debe ser numérico')
              .moreThan(0, 'Debe ser mayor a 0')
              .required('Campo obligatorio'),
            descripcion: yup.string().trim(),
          })}
          onSubmit={async (values, helpers) => {
            const success = await onSubmit(values)
            helpers.setSubmitting(false)
            if (success) handleClose()
          }}
        >
          {({ values }) => (
            <Form>
              <InputsWithValidate
                fields={[
                  {
                    class: 'textfield',
                    name: 'descripcion',
                    label: 'Descripción',
                    value: values.descripcion,
                    sm: 12,
                    xs: 12,
                    multiline: true,
                    rows: 2,
                  },
                  {
                    class: 'textfield',
                    name: 'importe',
                    label: 'Importe en pesos',
                    type: 'number',
                    value: values.importe,
                    sm: 12,
                    xs: 12,
                  },
                ]}
                spacing={2}
              />
            </Form>
          )}
        </Formik>
      }
      actions={
        <CancelConfirmButtons
          onClickSubmit={() => {
            if (formikRef.current) formikRef.current.submitForm()
          }}
          onClickCancel={handleClose}
          textButtonSubmit="CONFIRMAR"
        />
      }
    />
  )
}

const PaymentMethodContent = (props) => {
  const { data, setData, formikRef } = props

  const [edit] = useContext(EditContext)
  const createDismissableSnackbar = useCustomSnackbar()

  const { state: add, setOpen: openAdd, setClose: closeAdd } = SetState()
  const { state: editModal, setOpen: openEdit, setClose: closeEdit } = SetState()

  const [selectedRow, setSelectedRow] = useState(null)
  const [beforeData, setBeforeData] = useState(null)

  const empresaId = getEmpresaId(data?.paymentOrder?.origen)
  const orderId = toNumber(data?.paymentOrder?.id)
  const methodNameMap = useMemo(
    () => buildMethodNameMap(beforeData?.metodosPago || []),
    [beforeData]
  )

  const [rows, setRows] = useState(() =>
    (data?.paymentOrderDetails || []).map((row) => normalizeMethodRow(row, methodNameMap))
  )
  useEffect(() => {
    interfaces.salesPaymentOrders
      .getBeforeData()
      .then((res) => {
        setBeforeData(res?.data || null)
      })
      .catch(() => {
        setBeforeData(null)
      })
  }, [])

  useEffect(() => {
    setRows((data?.paymentOrderDetails || []).map((row) => normalizeMethodRow(row, methodNameMap)))
  }, [data?.paymentOrderDetails, methodNameMap])

  const methodOptions = useMemo(() => {
    const options = (beforeData?.metodosPago || [])
      .filter((item) => ALLOWED_METHODS.includes(item.id))
      .map((item) => ({ id: item.id, label: item.tipo }))

    if (options.length > 0) return options

    return [
      { id: 1, label: 'EFECTIVO' },
      { id: 2, label: 'TARJETA DÉBITO' },
      { id: 3, label: 'TARJETA CRÉDITO' },
      { id: 4, label: 'MERCADOPAGO' },
      { id: 6, label: 'DEPÓSITO' },
      { id: 7, label: 'TRANSFERENCIA' },
      { id: 8, label: 'MONEDA EXTRANJERA' },
      { id: 51, label: 'RETENCIÓN GANANCIAS' },
      { id: 60, label: 'OTROS INGRESOS' },
    ]
  }, [beforeData])

  const debitCards = useMemo(
    () =>
      (beforeData?.tarjetas || [])
        .filter((item) => item.tipo === 1)
        .map((item) => ({ id: item.id, label: item.nombre })),
    [beforeData]
  )

  const creditCards = useMemo(
    () =>
      (beforeData?.tarjetas || [])
        .filter((item) => item.tipo === 2)
        .map((item) => ({ id: item.id, label: item.nombre })),
    [beforeData]
  )

  const banks = useMemo(
    () => (beforeData?.bancos || []).map((item) => ({ id: item.id, label: item.nombre })),
    [beforeData]
  )

  const total = useMemo(
    () => (rows || []).reduce((acc, row) => acc + toNumber(row.importe), 0),
    [rows]
  )

  const refreshOrder = async () => {
    if (!orderId) return
    const updated = await interfaces.salesPaymentOrders.getOneForEdit(
      orderId,
      empresaId,
      data?.info || {}
    )
    setData(updated?.data || data)
  }

  const handleDelete = async (rowId) => {
    if (!edit || !orderId || !rowId) return

    try {
      await interfaces.salesPaymentOrders.deletePaymentOrderItem(rowId, empresaId, orderId)
      await refreshOrder()
      createDismissableSnackbar('Item eliminado con éxito', { variant: 'success' })
    } catch (error) {
      const msg = error?.response?.data?.data || error?.response?.data || 'Error al eliminar item'
      createDismissableSnackbar(msg, { variant: 'error' })
    }
  }

  const buildAddPayload = (values) => {
    const tipo = Number(values.metodo)

    const payload = {
      empresa: empresaId,
      orden: orderId,
      tipo,
      importe: toNumber(values.importe),
      descripcion: values.descripcion || '',
      detalle: values.descripcion || '',
    }

    if ([2, 3].includes(tipo)) {
      payload.origen = toNumber(values.tarjeta)
      payload.lote = toNumber(values.lote)
      payload.cupon = toNumber(values.cupon)
    }

    if (tipo === 4) {
      payload.cupon = toNumber(values.nro_operacion)
    }

    if ([6, 7].includes(tipo)) {
      payload.origen = toNumber(values.banco)
      payload.cupon = toNumber(values.nro_operacion)
    }

    if (tipo === 8) {
      const currency = CURRENCIES.find((item) => item.id === Number(values.moneda))
      payload.origen = toNumber(values.moneda)
      payload.moneda = currency?.label || ''
      payload.cupon = toNumber(values.cotizacion)
    }

    return payload
  }

  const addItem = async (values) => {
    if (!edit || !orderId) return false

    try {
      const payload = buildAddPayload(values)
      await interfaces.salesPaymentOrders.addPaymentOrderItem(payload)
      await refreshOrder()
      createDismissableSnackbar('Item agregado con éxito', { variant: 'success' })
      return true
    } catch (error) {
      const msg = error?.response?.data?.data || error?.response?.data || 'Error al agregar item'
      createDismissableSnackbar(msg, { variant: 'error' })
      return false
    }
  }

  const editItem = async (values) => {
    if (!selectedRow?.id || !orderId) return false

    try {
      await interfaces.salesPaymentOrders.editPaymentOrderItem({
        id: selectedRow.id,
        empresa: empresaId,
        orden: orderId,
        importe: toNumber(values.importe),
        descripcion: values.descripcion || '',
        detalle: values.descripcion || '',
      })
      await refreshOrder()
      createDismissableSnackbar('Item actualizado con éxito', { variant: 'success' })
      return true
    } catch (error) {
      const msg = error?.response?.data?.data || error?.response?.data || 'Error al actualizar item'
      createDismissableSnackbar(msg, { variant: 'error' })
      return false
    }
  }

  const tableRows = (rows || []).map((row) => ({
    metodo: row.metodo || methodNameMap[row.tipo] || '',
    descripcion: row.descripcion || '',
    monto: `$${toNumber(row.importe).toFixed(2)}`,
    action: (
      <IconButton
        onClick={(event) => {
          event.stopPropagation()
          handleDelete(row.id)
        }}
        disabled={!edit}
        style={{ padding: '0px' }}
      >
        <Delete />
      </IconButton>
    ),
    id: row.id,
  }))

  return (
    <Formik
      innerRef={formikRef}
      enableReinitialize
      initialValues={{ _dummy: '' }}
      validationSchema={yup.object().shape({ _dummy: yup.mixed() })}
      onSubmit={() => {
        setData((prev) => ({
          ...prev,
          paymentOrderDetails: rows,
          info: {
            ...(prev?.info || {}),
            importe_total: total,
          },
        }))
      }}
    >
      {() => (
        <Form>
          <div style={{ margin: '2rem' }}>
            <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'flex-start' }}>
              <InputsWithoutValidate
                fields={[
                  {
                    class: 'textfield',
                    name: 'importe_total',
                    label: 'Importe total',
                    value: total.toFixed(2),
                    disabled: true,
                    sm: 12,
                    xs: 12,
                  },
                ]}
                spacing={2}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
              <BasicTable
                col={[
                  { title: 'Medio de pago', style: { width: '180px' } },
                  { title: 'Descripción', style: { width: '260px' } },
                  { title: 'Importe en pesos', style: { width: '160px' } },
                  { title: '', style: { width: '40px' } },
                  { title: '', style: { display: 'none' } },
                ]}
                size="small"
                rows={tableRows}
                onClickRow={(row) => {
                  if (!edit) return
                  const selected = rows.find((item) => item.id === row.id)
                  setSelectedRow(selected || null)
                  openEdit(true)
                }}
              />

              <div style={{ marginLeft: '1rem' }}>
                <Button
                  onClick={openAdd}
                  disabled={!edit}
                  color="primary"
                  variant="contained"
                  size="large"
                  style={{ height: '40px' }}
                >
                  <AddIcon />
                </Button>
              </div>
            </div>
          </div>
          {add.suspense && (
            <AddPaymentMethod
              open={add.open}
              handleClose={closeAdd}
              onSubmit={addItem}
              methodOptions={methodOptions}
              debitCards={debitCards}
              creditCards={creditCards}
              banks={banks}
            />
          )}

          {editModal.suspense && (
            <EditPaymentMethod
              open={editModal.open}
              handleClose={closeEdit}
              row={selectedRow}
              onSubmit={editItem}
            />
          )}
        </Form>
      )}
    </Formik>
  )
}

export default PaymentMethodContent
